<template>
    <main id="trip-planning" class="trip-planning-wrapper">
        <div class="list-banner">
            <div class="overlay"></div>
            <h1 class="banner-title">Planning</h1>
        </div>

        <SubMenu parent-route-name="tripPlanning" />

        <div class="max-width-container">
            <div :class="['planning-content', { requesting: mode }]">
                <p v-if="!hasHomeCity && !mode" class="home-city-nudge">
                    📍 Tell us where you're based and your ideas will fit
                    better!
                    <router-link :to="{ name: 'settings' }"
                        >Set home city →</router-link
                    >
                </p>

                <div v-if="!mode" class="inspiration-fork">
                    <h2>Where to next?</h2>
                    <p class="fork-hint">
                        Get fresh ideas from your travels and bucket lists, or
                        tell us what you're after.
                    </p>
                    <div class="actions">
                        <Bttn
                            id="surprise-btn"
                            class="outlined ai"
                            icon="auto_awesome"
                            @click="surpriseMe"
                            >Surprise me</Bttn
                        >
                        <Bttn
                            id="specific-btn"
                            class="outlined"
                            icon="explore"
                            @click="beSpecific"
                            >I have something in mind</Bttn
                        >
                    </div>
                </div>

                <div v-else-if="editing" class="request-card">
                    <div class="row-detail-data">
                        <div class="user-data-item">
                            <span class="icon material-symbols-outlined">
                                schedule
                            </span>
                            <div>
                                <span class="title">Trip length (days)</span>
                                <div class="user-input">
                                    <InlineChoice
                                        id="duration-select"
                                        v-model="selectedDuration"
                                        group="travel_duration"
                                        :disabled-options="disabledDurations"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="user-data-item">
                            <span class="icon material-symbols-outlined">
                                payments
                            </span>
                            <div>
                                <span class="title">Budget</span>
                                <div class="user-input">
                                    <InlineChoice
                                        id="style-select"
                                        v-model="selectedStyle"
                                        group="travel_style"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="user-data-item full-width">
                            <span class="icon material-symbols-outlined">
                                calendar_month
                            </span>
                            <div>
                                <span class="title">Travel months</span>
                                <div class="user-input">
                                    <InlineChoice
                                        id="months-select"
                                        v-model="selectedMonths"
                                        group="travel_months"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="user-data-item full-width">
                            <span class="icon material-symbols-outlined">
                                public
                            </span>
                            <div>
                                <span class="title">Where in the world</span>
                                <div class="user-input">
                                    <InlineChoice
                                        id="region-select"
                                        v-model="selectedRegion"
                                        group="travel_region"
                                        :inline-max="30"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="actions">
                        <Bttn
                            id="back-btn"
                            class="outlined"
                            icon="arrow_back"
                            @click="goBack"
                            >Back</Bttn
                        >
                        <Bttn
                            id="get-recommendations-btn"
                            class="outlined ai"
                            icon="auto_awesome"
                            @click="sendRequest"
                            >Find my trips</Bttn
                        >
                    </div>
                </div>

                <div v-else class="request-summary">
                    <div class="summary-tags">
                        <span
                            v-for="tag in requestTags"
                            :key="tag.title"
                            class="summary-tag"
                            :title="tag.title"
                        >
                            <span class="material-symbols-outlined">{{
                                tag.icon
                            }}</span>
                            {{ tag.label }}
                        </span>
                    </div>
                    <div class="actions">
                        <Bttn
                            class="outlined ai slim"
                            icon="refresh"
                            :disabled="loading"
                            @click="fetchRecommendations(true)"
                            >More</Bttn
                        >
                        <Bttn
                            v-if="mode === 'specific'"
                            class="outlined slim ai"
                            icon="auto_awesome"
                            :disabled="loading"
                            @click="surpriseMe"
                            >Surprise me</Bttn
                        >
                        <Bttn class="outlined slim" @click="beSpecific">{{
                            mode === "surprise" ? "Be more specific" : "Edit"
                        }}</Bttn>
                    </div>
                </div>

                <div v-if="loading" ref="loader" class="loading-state">
                    <p>Putting a few ideas together for you…</p>
                </div>

                <div v-else-if="error" class="error-state">
                    <p>{{ error }}</p>
                    <Bttn @click="fetchRecommendations(true)">Try Again</Bttn>
                </div>

                <div
                    v-else-if="recommendations.length > 0"
                    class="recommendations-container"
                >
                    <nav class="planning-nav">
                        <ul>
                            <li
                                v-for="[duration, index] in durations"
                                :key="index"
                                class="duration-nav-item"
                            >
                                <a
                                    :href="'#duration-' + index"
                                    @click.prevent="
                                        scrollTo('#duration-' + index)
                                    "
                                    >{{ duration }}</a
                                >
                                <ul class="trip-nav-list">
                                    <li
                                        v-for="(
                                            rec, tripIdx
                                        ) in recommendations[index].trips"
                                        :key="tripIdx"
                                    >
                                        <a
                                            :href="
                                                '#trip-' + index + '-' + tripIdx
                                            "
                                            @click.prevent="
                                                scrollTo(
                                                    '#trip-' +
                                                        index +
                                                        '-' +
                                                        tripIdx
                                                )
                                            "
                                        >
                                            {{ rec.title }}
                                        </a>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </nav>

                    <div class="recommendations">
                        <p v-if="saveError" class="planning-action-error">
                            {{ saveError }}
                        </p>
                        <div
                            v-for="[duration, index] in durations"
                            :id="'duration-' + index"
                            :key="index"
                        >
                            <h2>{{ duration }}</h2>

                            <IdeaItem
                                v-for="(rec, tripIdx) in recommendations[index]
                                    .trips"
                                :id="'trip-' + index + '-' + tripIdx"
                                :key="tripIdx"
                                :idea="rec"
                            >
                                <template #actions>
                                    <Bttn
                                        class="outlined"
                                        :icon="
                                            isSaved(rec)
                                                ? 'check'
                                                : 'favorite_border'
                                        "
                                        :disabled="
                                            isCurrentlySaving(rec) ||
                                            isSaved(rec)
                                        "
                                        :icon-only="true"
                                        :title="saveLabel(rec)"
                                        :aria-label="saveLabel(rec)"
                                        @click="saveTripIdea(rec)"
                                    />
                                </template>
                                <a
                                    href="#trip-planning"
                                    class="back-to-top"
                                    @click.prevent="scrollTo('#trip-planning')"
                                >
                                    <span class="material-symbols-outlined"
                                        >arrow_upward</span
                                    >
                                    Back to top
                                </a>
                            </IdeaItem>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import { useSavedIdeaStore } from "js/stores/savedIdeaStore";
import { useRecommendationStore } from "js/stores/recommendationStore";
import { useUtilsStore } from "js/stores/utilsStore";
import { useUserStore } from "js/stores/userStore";
import { ServerError } from "js/lib/server.js";
import IdeaItem from "js/components/list-items/IdeaItem.vue";
import InlineChoice from "js/components/common/InlineChoice.vue";

export default {
    name: "TripInspirations",
    components: { IdeaItem, InlineChoice },
    data() {
        return {
            isImageLoadingCancelled: false,
            loading: false,
            error: null,
            saveError: null,
            savingTitles: [],
            savedIdeaStore: null,
            recommendationStore: null,
            utilsStore: null,
            userStore: null,
            editing: false,
        };
    },
    computed: {
        recommendations: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.recommendations
                    : [];
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.recommendations = val;
            },
        },
        durations: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.durations
                    : [];
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.durations = val;
            },
        },
        selectedStyle: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.selectedStyle
                    : null;
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.selectedStyle = val;
            },
        },
        selectedMonths: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.selectedMonths
                    : null;
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.selectedMonths = val;
            },
        },
        selectedDuration: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.selectedDuration
                    : null;
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.selectedDuration = val;
            },
        },
        selectedRegion: {
            get() {
                return this.recommendationStore
                    ? this.recommendationStore.selectedRegion
                    : null;
            },
            set(val) {
                if (this.recommendationStore)
                    this.recommendationStore.selectedRegion = val;
            },
        },
        requestQuery() {
            const query = {};
            const filters = {
                duration: this.selectedDuration,
                months: this.selectedMonths,
                region: this.selectedRegion,
                style: this.selectedStyle,
            };
            Object.entries(filters).forEach(([key, value]) => {
                if (value) query[key] = value;
            });
            return query;
        },
        mode() {
            return this.recommendationStore
                ? this.recommendationStore.mode
                : null;
        },
        hasHomeCity() {
            return !this.userStore || Boolean(this.userStore.user.geo);
        },
        disabledDurations() {
            return this.hasHomeCity
                ? {}
                : {
                      1: "Day trips need your home city. Set it in your settings.",
                  };
        },
        requestTags() {
            if (this.mode === "surprise") {
                return [
                    {
                        icon: "auto_awesome",
                        title: "Surprise",
                        label: "Surprise picks from your travels and bucket lists",
                    },
                ];
            }
            const tags = [
                this.selectedDuration && {
                    icon: "schedule",
                    title: "Trip length",
                    label: `${this.selectedDuration} ${
                        this.selectedDuration === "1" ? "day" : "days"
                    }`,
                },
                this.selectedMonths && {
                    icon: "calendar_month",
                    title: "Travel months",
                    label: this.$enumGet("travel_months", this.selectedMonths),
                },
                this.selectedRegion && {
                    icon: "public",
                    title: "Region",
                    label: this.$enumGet("travel_region", this.selectedRegion),
                },
                this.selectedStyle && {
                    icon: "payments",
                    title: "Budget",
                    label: this.$enumGet("travel_style", this.selectedStyle),
                },
            ].filter(Boolean);
            return tags.length
                ? tags
                : [
                      {
                          icon: "explore",
                          title: "Open",
                          label: "Open to anything",
                      },
                  ];
        },
    },
    watch: {
        requestQuery() {
            this.syncQuery();
        },
        disabledDurations(disabled) {
            if (disabled[this.selectedDuration]) {
                this.selectedDuration = null;
            }
        },
    },
    mounted() {
        this.savedIdeaStore = useSavedIdeaStore();
        this.savedIdeaStore.load();
        this.recommendationStore = useRecommendationStore();
        this.utilsStore = useUtilsStore();
        this.userStore = useUserStore();
        this.loadRequestFromQuery();
        this.syncQuery();
        this.editing =
            this.mode === "specific" && this.recommendations.length === 0;
    },
    beforeUnmount() {
        this.isImageLoadingCancelled = true;
    },
    methods: {
        loadRequestFromQuery() {
            const filters = {
                duration: ["selectedDuration", "travel_duration"],
                months: ["selectedMonths", "travel_months"],
                region: ["selectedRegion", "travel_region"],
                style: ["selectedStyle", "travel_style"],
            };
            let loaded = false;
            Object.entries(filters).forEach(([key, [field, group]]) => {
                const value = this.$route.query[key];
                const isValid = this.$enumOptions(group).some(
                    ([option]) => option === value
                );
                if (
                    isValid &&
                    !(key === "duration" && this.disabledDurations[value])
                ) {
                    this[field] = value;
                    loaded = true;
                }
            });
            if (loaded) {
                this.recommendationStore.mode = "specific";
            }
        },
        syncQuery() {
            this.$router.replace({ query: this.requestQuery });
        },
        delay(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        surpriseMe() {
            this.selectedStyle = null;
            this.selectedMonths = null;
            this.selectedDuration = null;
            this.selectedRegion = null;
            this.recommendationStore.mode = "surprise";
            this.editing = false;
            this.fetchRecommendations();
        },
        beSpecific() {
            this.recommendationStore.mode = "specific";
            this.editing = true;
        },
        sendRequest() {
            this.editing = false;
            this.fetchRecommendations();
        },
        scrollToLoader() {
            this.$nextTick(() => {
                if (window.matchMedia("(max-width: 768px)").matches) {
                    this.$refs.loader?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            });
        },
        goBack() {
            this.selectedStyle = null;
            this.selectedMonths = null;
            this.selectedDuration = null;
            this.selectedRegion = null;
            this.recommendationStore.mode = null;
            this.recommendationStore.recommendations = [];
            this.editing = false;
            this.$router.replace({ query: {} });
        },
        async fetchRecommendations(reset = false) {
            this.loading = true;
            this.error = null;
            this.scrollToLoader();

            try {
                const params = {
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                };
                if (reset) {
                    params.reset = 1;
                }
                if (this.selectedStyle) {
                    params.style = this.selectedStyle;
                }
                if (this.selectedMonths) {
                    params.months = this.selectedMonths;
                }
                if (this.selectedDuration) {
                    params.duration = this.selectedDuration;
                }
                if (this.selectedRegion) {
                    params.region = this.selectedRegion;
                }

                try {
                    this.recommendations =
                        await this.recommendationStore.loadTripIdeas(params);
                } catch (err) {
                    const canRetryFast =
                        err.response?.status === 504 &&
                        err.response?.data?.fast_available !== false;
                    if (!canRetryFast) {
                        throw err;
                    }
                    this.recommendations =
                        await this.recommendationStore.loadTripIdeas({
                            ...params,
                            fast: 1,
                        });
                }
                this.durations = this.recommendations.map((rec, i) => {
                    return [rec.duration, i];
                });
                this.$nextTick(() => {
                    this.loadImages();
                });
            } catch (err) {
                if (err instanceof ServerError && err.status === 429) {
                    this.error =
                        err.data?.error === "ai_budget_exhausted"
                            ? err.data.message
                            : err.message;
                } else {
                    this.error =
                        "Failed to fetch recommendations. We are currently using free-tier models, which may be busy. Please try again.";
                }
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
        scrollTo(selector) {
            const el = document.querySelector(selector);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        },
        async saveTripIdea(rec) {
            if (!this.savingTitles.includes(rec.title)) {
                this.savingTitles.push(rec.title);
            }
            this.saveError = null;
            try {
                await this.savedIdeaStore.save(rec);
            } catch (err) {
                this.saveError = "Failed to save trip idea. Please try again.";
                console.error(err);
            } finally {
                this.savingTitles = this.savingTitles.filter(
                    (t) => t !== rec.title
                );
            }
        },
        async loadImages() {
            this.isImageLoadingCancelled = false;
            for (const rec of this.recommendations) {
                for (const trip of rec.trips) {
                    try {
                        trip.pexel_images = await this.utilsStore.pexelsImages(
                            trip.image_query
                        );
                    } catch {
                        return;
                    }
                    await this.delay(500);
                }
            }
        },
        isCurrentlySaving(rec) {
            return this.savingTitles.includes(rec.title);
        },
        saveLabel(rec) {
            if (this.isSaved(rec)) return "Saved";
            return this.isCurrentlySaving(rec) ? "Saving..." : "Save Idea";
        },
        isSaved(rec) {
            if (!this.savedIdeaStore) return false;
            return this.savedIdeaStore.find(rec.title) !== null;
        },
    },
};
</script>
